import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import '../scss/kaart.scss';
import { createIcon } from './icons';
import { insetLimits, intersectLimits, placeCallout, type Limits } from './place';
import { isCategory, categoryClass, type MapData, type Point } from './types';

const MARKER = 28;
const DOT_RADIUS = 9;
const LINE_TUCK = 14;
const TITLE_WIDTH = 168;

function boot(): void {
	const cleanups = new Map<HTMLElement, () => void>();

	const mount = (root: HTMLElement): void => {
		if (cleanups.has(root)) {
			return;
		}
		const destroy = start(root);
		if (destroy === null) {
			return;
		}
		cleanups.set(root, destroy);
	};

	const unmount = (root: HTMLElement): void => {
		const destroy = cleanups.get(root);
		if (destroy === undefined) {
			return;
		}
		destroy();
		cleanups.delete(root);
	};

	const scanAdded = (node: Node): void => {
		if (!(node instanceof HTMLElement)) {
			return;
		}
		if (node.matches('[data-tulips-kaart]')) {
			mount(node);
		}
		node.querySelectorAll<HTMLElement>('[data-tulips-kaart]').forEach(mount);
	};

	const scanRemoved = (node: Node): void => {
		if (!(node instanceof HTMLElement)) {
			return;
		}
		if (node.matches('[data-tulips-kaart]')) {
			unmount(node);
		}
		node.querySelectorAll<HTMLElement>('[data-tulips-kaart]').forEach(unmount);
	};

	document.querySelectorAll<HTMLElement>('[data-tulips-kaart]').forEach(mount);

	const observer = new MutationObserver((mutations) => {
		for (const mutation of mutations) {
			mutation.addedNodes.forEach(scanAdded);
			mutation.removedNodes.forEach(scanRemoved);
		}
	});
	observer.observe(document.documentElement, { childList: true, subtree: true });
}

function start(root: HTMLElement): (() => void) | null {
	const viewport = root.querySelector<HTMLElement>('.tulips-kaart__viewport');
	const dataNode = root.querySelector<HTMLScriptElement>('.tulips-kaart__data');
	const dialog = root.querySelector<HTMLDialogElement>('.tulips-kaart__popup');
	if (viewport === null || dataNode === null || dataNode.textContent === null || dialog === null) {
		return null;
	}

	const data = parseData(dataNode.textContent);
	if (data === null) {
		return null;
	}

	const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
	const bounds = L.latLngBounds([0, 0], [data.height, data.width]);
	const map = L.map(viewport, {
		crs: L.CRS.Simple,
		attributionControl: false,
		zoomSnap: 0,
		zoomDelta: 0.5,
		minZoom: -6,
		maxZoom: 2,
		maxBounds: bounds,
		maxBoundsViscosity: 1,
		bounceAtZoomLimits: false,
		scrollWheelZoom: true,
	});

	let didFit = false;
	const fitInitial = (): void => {
		if (didFit || viewport.clientWidth === 0 || viewport.clientHeight === 0) {
			return;
		}
		map.invalidateSize();
		map.fitBounds(bounds, { animate: false, padding: [0, 0] });
		const fitted = map.getBoundsZoom(bounds, false);
		map.setMinZoom(fitted);
		if (map.getZoom() < fitted) {
			map.setZoom(fitted);
		}
		didFit = true;
	};
	fitInitial();

	L.imageOverlay(data.image, bounds).addTo(map);

	const popupTitle = dialog.querySelector<HTMLElement>('.tulips-kaart__popup__panel__title');
	const popupTitleText = dialog.querySelector<HTMLElement>('.tulips-kaart__popup__panel__title__text');
	const popupBody = dialog.querySelector<HTMLElement>('.tulips-kaart__popup__panel__body');
	const popupButton = dialog.querySelector<HTMLAnchorElement>('.tulips-kaart__popup__panel__button');
	const popupClose = dialog.querySelector<HTMLButtonElement>('.tulips-kaart__popup__panel__close');

	let openPoint: Point | null = null;
	let frame = 0;

	const markers = new Map<string, { point: Point; marker: L.Marker; button: HTMLButtonElement; callout: HTMLElement; badge: HTMLElement; title: HTMLElement | null; line: HTMLElement }>();
	const cancelCloses: Array<() => void> = [];
	const looseButtons: Array<{ marker: L.Marker; button: HTMLButtonElement }> = [];
	const attachButtons = (): void => {
		for (const item of looseButtons) {
			const element = item.marker.getElement();
			if (element !== undefined && element !== null && !element.contains(item.button)) {
				element.append(item.button);
				L.DomEvent.disableClickPropagation(element);
			}
		}
	};

	const legendButtons = [...root.querySelectorAll<HTMLButtonElement>('.tulips-kaart__legend__domain__list__item__button')];
	const markLegend = (id: string | null): void => {
		for (const button of legendButtons) {
			const active = id !== null && button.dataset.pointId === id;
			button.classList.toggle('tulips-kaart__legend__domain__list__item__button--is-active', active);
			button.setAttribute('aria-pressed', active ? 'true' : 'false');
		}
	};

	const closeCallout = (): void => {
		if (openPoint === null) {
			return;
		}
		const current = markers.get(openPoint.id);
		openPoint = null;
		markLegend(null);
		if (current === undefined) {
			return;
		}
		current.button.classList.remove('tulips-kaart__hotspot--is-open');
		current.button.setAttribute('aria-expanded', 'false');
		current.marker.setZIndexOffset(0);
	};

	const positionCallout = (point: Point): void => {
		const entry = markers.get(point.id);
		if (entry === undefined) {
			return;
		}
		const anchor = map.latLngToContainerPoint(toLatLng(point, data.height));
		const limits = insetLimits(visibleLimits(map, data.width, data.height), 8);
		if (limits.right <= limits.left || limits.bottom <= limits.top) {
			return;
		}

		entry.title?.style.setProperty('width', `${Math.max(TITLE_WIDTH, entry.badge.offsetWidth)}px`);
		const badgeSize = { width: entry.badge.offsetWidth, height: entry.badge.offsetHeight };
		const titleSize =
			entry.title === null
				? { width: 0, height: 0 }
				: { width: entry.title.offsetWidth, height: entry.title.offsetHeight };
		const placement = placeCallout({
			pointX: anchor.x,
			pointY: anchor.y,
			badge: badgeSize,
			title: titleSize,
			limits,
		});

		const badgeX = placement.badgeX - anchor.x;
		const badgeY = placement.badgeY - anchor.y;
		entry.badge.style.left = `${badgeX}px`;
		entry.badge.style.top = `${badgeY}px`;
		if (entry.title !== null) {
			entry.title.style.left = `${placement.titleX - anchor.x}px`;
			entry.title.style.top = `${placement.titleY - anchor.y}px`;
		}

		const edgeX = placement.lineX - anchor.x;
		const edgeY = placement.lineY - anchor.y;
		const intoX = placement.badgeX + badgeSize.width / 2 - placement.lineX;
		const intoY = placement.badgeY + badgeSize.height / 2 - placement.lineY;
		const into = Math.hypot(intoX, intoY) || 1;
		const endX = edgeX + (intoX / into) * LINE_TUCK;
		const endY = edgeY + (intoY / into) * LINE_TUCK;
		const length = Math.hypot(endX, endY) || 1;
		const angle = Math.atan2(endY, endX);
		entry.line.style.left = `${Math.cos(angle) * DOT_RADIUS}px`;
		entry.line.style.top = `${Math.sin(angle) * DOT_RADIUS}px`;
		entry.line.style.width = `${Math.max(0, length - DOT_RADIUS)}px`;
		entry.line.style.transform = `rotate(${angle}rad)`;
	};

	const openCallout = (point: Point): void => {
		if (point.category === null && point.title === '') {
			return;
		}
		if (openPoint?.id !== point.id) {
			closeCallout();
		}
		const entry = markers.get(point.id);
		if (entry === undefined) {
			return;
		}
		openPoint = point;
		positionCallout(point);
		entry.button.classList.add('tulips-kaart__hotspot--is-open');
		entry.button.setAttribute('aria-expanded', 'true');
		entry.marker.setZIndexOffset(1000);
		markLegend(point.id);
	};

	const setPopupIcon = (category: Point['category']): void => {
		popupTitle?.querySelector('.tulips-kaart__popup__panel__title__icon')?.remove();
		if (category === null || popupTitle === null) {
			return;
		}
		const icon = createIcon(category);
		const name = category === 'carbon_removal' ? 'carbon-removal' : category;
		icon.setAttribute('class', `tulips-kaart__popup__panel__title__icon tulips-kaart__popup__panel__title__icon--${name}`);
		popupTitle.prepend(icon);
	};

	const act = (point: Point): void => {
		if (point.action === 'link' && point.url !== '') {
			if (point.target === '_blank') {
				window.open(point.url, '_blank', 'noopener,noreferrer');
			} else {
				window.location.assign(point.url);
			}
			return;
		}
		if (point.action !== 'popup' || popupTitleText === null || popupBody === null || popupButton === null) {
			return;
		}
		setPopupIcon(point.category);
		popupTitleText.textContent = point.title;
		popupBody.innerHTML = point.popupHtml;
		if (point.buttonUrl !== '') {
			popupButton.hidden = false;
			popupButton.href = point.buttonUrl;
			popupButton.textContent = point.buttonTitle !== '' ? point.buttonTitle : point.buttonUrl;
			if (point.buttonTarget === '_blank') {
				popupButton.target = '_blank';
				popupButton.rel = 'noopener noreferrer';
			} else {
				popupButton.removeAttribute('target');
				popupButton.removeAttribute('rel');
			}
		} else {
			popupButton.hidden = true;
			popupButton.removeAttribute('href');
		}
		if (!dialog.open) {
			dialog.showModal();
		}
	};

	for (const point of data.points) {
		const button = document.createElement('button');
		button.type = 'button';
		button.className = 'tulips-kaart__hotspot';
		button.setAttribute('aria-expanded', 'false');
		button.setAttribute('aria-label', point.title !== '' ? point.title : 'Hotspot');

		const ringA = document.createElement('span');
		ringA.className = 'tulips-kaart__hotspot__ring';
		const ringB = document.createElement('span');
		ringB.className = 'tulips-kaart__hotspot__ring tulips-kaart__hotspot__ring--late';
		const dot = document.createElement('span');
		dot.className = 'tulips-kaart__hotspot__dot';
		button.append(ringA, ringB, dot);

		const callout = document.createElement('span');
		callout.className = 'tulips-kaart__hotspot__callout';
		const line = document.createElement('span');
		line.className = 'tulips-kaart__hotspot__callout__line';
		const badge = document.createElement('span');
		badge.className = 'tulips-kaart__hotspot__callout__badge';
		if (point.category !== null) {
			badge.classList.add(categoryClass(point.category));
			badge.append(createIcon(point.category));
		}
		if (point.code !== '') {
			const code = document.createElement('span');
			code.className = 'tulips-kaart__hotspot__callout__badge__code';
			code.textContent = point.code;
			badge.append(code);
		}
		callout.append(line, badge);

		let title: HTMLElement | null = null;
		if (point.title !== '') {
			title = document.createElement('span');
			title.className = 'tulips-kaart__hotspot__callout__title';
			title.textContent = point.title;
			callout.append(title);
		}
		button.append(callout);

		const marker = L.marker(toLatLng(point, data.height), {
			icon: L.divIcon({
				className: 'tulips-kaart__marker',
				html: '',
				iconSize: [MARKER, MARKER],
				iconAnchor: [MARKER / 2, MARKER / 2],
			}),
			keyboard: false,
		}).addTo(map);

		const element = marker.getElement();
		if (element !== undefined && element !== null) {
			element.append(button);
			L.DomEvent.disableClickPropagation(element);
		} else {
			looseButtons.push({ marker, button });
		}

		let closeTimer = 0;
		const cancelClose = (): void => {
			window.clearTimeout(closeTimer);
		};
		cancelCloses.push(cancelClose);
		const scheduleClose = (): void => {
			cancelClose();
			closeTimer = window.setTimeout(() => {
				if (openPoint?.id === point.id) {
					closeCallout();
				}
			}, 280);
		};

		button.addEventListener('click', (event) => {
			event.preventDefault();
			if (finePointer) {
				act(point);
				return;
			}
			if (openPoint?.id === point.id) {
				act(point);
				return;
			}
			openCallout(point);
		});

		if (finePointer) {
			button.addEventListener('mouseenter', () => {
				cancelClose();
				openCallout(point);
			});
			button.addEventListener('mouseleave', () => {
				scheduleClose();
			});
			button.addEventListener('focus', () => {
				openCallout(point);
			});
			button.addEventListener('blur', () => {
				if (openPoint?.id === point.id) {
					closeCallout();
				}
			});
		}

		markers.set(point.id, { point, marker, button, callout, badge, title, line });
	}
	attachButtons();

	root.querySelector('.tulips-kaart__legend')?.addEventListener('click', (event) => {
		const target = event.target;
		if (!(target instanceof Element)) {
			return;
		}
		const contentButton = target.closest('.tulips-kaart__legend__domain__popups__item__button');
		if (contentButton instanceof HTMLButtonElement && root.contains(contentButton)) {
			const content = contentButton.parentElement?.querySelector('.tulips-kaart__legend__domain__popups__item__content');
			if (popupTitleText === null || popupBody === null || popupButton === null) {
				return;
			}
			event.preventDefault();
			setPopupIcon(null);
			popupTitleText.textContent = contentButton.textContent?.trim() ?? '';
			popupBody.innerHTML = content instanceof HTMLElement ? content.innerHTML : '';
			popupButton.hidden = true;
			popupButton.removeAttribute('href');
			if (!dialog.open) {
				dialog.showModal();
			}
			return;
		}
		const button = target.closest('button[data-point-id]');
		if (!(button instanceof HTMLButtonElement) || !root.contains(button)) {
			return;
		}
		const point = data.points.find((item) => item.id === button.dataset.pointId);
		if (point === undefined) {
			return;
		}
		event.preventDefault();
		cancelCloses.forEach((cancel) => {
			cancel();
		});
		const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
		viewport.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
		map.panTo(toLatLng(point, data.height), { animate: !reduceMotion });
		openCallout(point);
	});

	map.on('click', () => {
		closeCallout();
	});

	const refitMinZoom = (): void => {
		if (!didFit || viewport.clientWidth === 0 || viewport.clientHeight === 0) {
			return;
		}
		const fitted = map.getBoundsZoom(bounds, false);
		map.setMinZoom(fitted);
		if (map.getZoom() < fitted) {
			map.setZoom(fitted);
		}
	};

	const onViewChange = (): void => {
		cancelAnimationFrame(frame);
		frame = requestAnimationFrame(() => {
			if (openPoint !== null) {
				positionCallout(openPoint);
			}
		});
	};

	map.on('move zoom resize', onViewChange);
	const onResize = (): void => {
		map.invalidateSize();
		if (!didFit) {
			fitInitial();
			attachButtons();
			return;
		}
		refitMinZoom();
		onViewChange();
	};
	window.addEventListener('resize', onResize);

	popupClose?.addEventListener('click', () => {
		dialog.close();
	});
	dialog.addEventListener('click', (event) => {
		if (event.target === dialog) {
			dialog.close();
		}
	});

	return () => {
		cancelAnimationFrame(frame);
		cancelCloses.forEach((cancel) => {
			cancel();
		});
		window.removeEventListener('resize', onResize);
		if (dialog.open) {
			dialog.close();
		}
		map.remove();
	};
}

function toLatLng(point: Point, height: number): L.LatLng {
	return L.latLng(height - point.y, point.x);
}

function visibleLimits(map: L.Map, width: number, height: number): Limits {
	const northWest = map.latLngToContainerPoint(L.latLng(height, 0));
	const southEast = map.latLngToContainerPoint(L.latLng(0, width));
	const size = map.getSize();
	return intersectLimits(
		{ left: northWest.x, top: northWest.y, right: southEast.x, bottom: southEast.y },
		{ left: 0, top: 0, right: size.x, bottom: size.y },
	);
}

function parseData(raw: string): MapData | null {
	let value: unknown;
	try {
		value = JSON.parse(raw) as unknown;
	} catch {
		return null;
	}
	if (!isRecord(value) || typeof value.image !== 'string' || typeof value.width !== 'number' || typeof value.height !== 'number' || !Array.isArray(value.points)) {
		return null;
	}

	const points: Point[] = [];
	for (const item of value.points) {
		const point = parsePoint(item);
		if (point !== null) {
			points.push(point);
		}
	}

	return {
		image: value.image,
		width: value.width,
		height: value.height,
		points,
	};
}

function parsePoint(value: unknown): Point | null {
	if (!isRecord(value) || typeof value.id !== 'string' || typeof value.x !== 'number' || typeof value.y !== 'number') {
		return null;
	}
	const category = typeof value.category === 'string' && isCategory(value.category) ? value.category : null;
	const action = value.action === 'link' || value.action === 'popup' ? value.action : null;
	return {
		id: value.id,
		x: value.x,
		y: value.y,
		category,
		code: stringField(value.code),
		title: stringField(value.title),
		action,
		url: stringField(value.url),
		target: stringField(value.target),
		popupHtml: stringField(value.popupHtml),
		buttonUrl: stringField(value.buttonUrl),
		buttonTitle: stringField(value.buttonTitle),
		buttonTarget: stringField(value.buttonTarget),
	};
}

function stringField(value: unknown): string {
	return typeof value === 'string' ? value : '';
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === 'object' && value !== null;
}

boot();
